import React from 'react';
import { delightMessageMetaData } from 'helpers/tracking.helpers';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Button } from 'components/form/Button';
import SectionContainer from 'components/Containers/SectionContainer';
import { Row } from 'components/Containers/Row/Row';
import VisibilitySensor from 'react-visibility-sensor';
import '../../../../components/Image/Image.scss';
import './RentPaymentDashboard.scss';
import { useViewModule, usePageView } from 'hook/helper.hook';
import { ReactComponent as RentPayment } from 'components/premium/Turbo/assets/art.svg';
import { Tag } from 'components/Tag';
import { ReactComponent as ArrowRight } from 'partial/OfferManager/modules/RentPaymentDashboard/assets/chevron_right.svg';
import { ReactComponent as PaymentPosted } from 'partial/OfferManager/modules/RentPaymentDashboard/assets/PaymentPosted.svg';
import { ReactComponent as PaymentPending } from 'partial/OfferManager/modules/RentPaymentDashboard/assets/PaymentPending.svg';
import { ReactComponent as BankAccountDisconnected } from 'partial/OfferManager/modules/RentPaymentDashboard/assets/warning.svg';
import { ReactComponent as LightBulb } from 'partial/OfferManager/modules/RentPaymentDashboard/assets/art-lightbulb.svg';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { Link } from 'components/navigation/Link/Link';

import {
  IN_PROGRESS,
  ENROLLED,
  LANDLORD_DETAILS_NEEDED,
  RENTAL_DETAILS_NEEDED,
  BANK_ACCOUNT_NOT_LINKED,
  BANK_ACCOUNT_LINKED,
  IDENTIFY_PAYMENTS,
  READY_FOR_VERIFICATION,
  PUSHED_TO_SECONDARY_VERIFICATION,
  WAITING_FOR_INFORMATION,
  WAITING_FOR_PAYMENT,
  UNENROLLED,
  VOR_SENT,
  CALL_TO_VERIFY,
  ESCALATED_VOR,
  UNABLE_TO_VERIFY,
  READY_FOR_SUBMISSION,
} from 'types/rentReporting.constants';
import classnames from 'classnames';
import { createLogger } from 'helpers/logger';
import { formatAsMonthDayYear } from 'helpers/dateHelper';
import { fixedNumOfLetters } from 'helpers/string.helper';
import { formatIncome } from 'helpers/money.helper';
import RowBox from 'components/Containers/RowBox';
import Label from 'components/form/Label/Label';
import { getPremiumPlanUiDisplayName } from 'helpers/userHelper';
import { useConfig } from 'hook/config.hook';

const logger = createLogger({
  name: 'RentPaymentDashboard.cmp',
});

const RentPaymentBody = props => {
  const enrollmentStatus = props?.authResponse?.enrollmentStatus;
  const rentReportingStatus =
    enrollmentStatus?.status || props?.userInfo?.rentReporting?.status;
  const rentReportingSubStatus =
    enrollmentStatus?.subStatus || props?.userInfo?.rentReporting?.subStatus;
  //POD2-1364
  const ModulePageObject = {
    'Module Name': 'Rent Payment Status and Substatus',
    'Module Position': props.modulePosition,
    Status: rentReportingStatus,
    SubStatus: rentReportingSubStatus,
  };
  useViewModule(ModulePageObject, !!rentReportingSubStatus);
  usePageView(!rentReportingSubStatus, ModulePageObject);

  const hasDisconnectedBank = props?.paymentsResponse?.hasDisconnectedBanks;
  const missingPayments = props?.paymentsResponse?.numMissingPayments;

  const isRentReportingIncluded =
    props?.userInfo?.premiumPlanInfo?.rentReportingIncluded;

  if (isRentReportingIncluded && !rentReportingStatus) {
    return <RentPaymentEnrollment {...props} />;
  } else if (rentReportingStatus === IN_PROGRESS) {
    if (
      rentReportingSubStatus === LANDLORD_DETAILS_NEEDED ||
      rentReportingSubStatus === RENTAL_DETAILS_NEEDED ||
      rentReportingSubStatus === BANK_ACCOUNT_NOT_LINKED ||
      rentReportingSubStatus === BANK_ACCOUNT_LINKED ||
      rentReportingSubStatus === IDENTIFY_PAYMENTS
    ) {
      if (hasDisconnectedBank) {
        return (
          <RentPaymentInProgressPaymentIdentified
            {...props}
            hideButton={false}
          />
        );
      } else {
        return (
          <RentPaymentSignUp
            rentReportingStatus={rentReportingStatus}
            rentReportingSubStatus={rentReportingSubStatus}
            {...props}
          />
        );
      }
    } else if (
      rentReportingSubStatus === READY_FOR_VERIFICATION ||
      rentReportingSubStatus === PUSHED_TO_SECONDARY_VERIFICATION ||
      rentReportingSubStatus === CALL_TO_VERIFY
    ) {
      if (hasDisconnectedBank) {
        return (
          <RentPaymentInProgressPaymentIdentified
            {...props}
            hideButton={false}
          />
        );
      } else {
        return (
          <RentPaymentInProgressPaymentIdentified
            {...props}
            hideButton={true}
          />
        );
      }
    } else if (
      rentReportingSubStatus === VOR_SENT ||
      rentReportingSubStatus === ESCALATED_VOR
    ) {
      return (
        <RentPaymentInProgressPaymentIdentified
          {...props}
          hideButton={true}
          hasDisconnectedBank={hasDisconnectedBank}
        />
      );
    } else if (rentReportingSubStatus === WAITING_FOR_INFORMATION) {
      return (
        <RentPaymentInProgressPaymentIdentified {...props} hideButton={false} />
      );
    } else if (rentReportingSubStatus === WAITING_FOR_PAYMENT) {
      return (
        <RentPaymentInProgressPaymentIdentified {...props} hideButton={false} />
      );
    } else if (rentReportingSubStatus === UNABLE_TO_VERIFY) {
      return (
        <RentPaymentInProgressPaymentIdentified
          {...props}
          hideButton={true}
          customButtom={true}
        />
      );
    } else {
      return '';
    }
  } else if (rentReportingStatus === ENROLLED) {
    if (rentReportingSubStatus === WAITING_FOR_PAYMENT) {
      if (hasDisconnectedBank) {
        return (
          <RentPaymentInProgressPaymentIdentified
            {...props}
            hideButton={false}
            hasDisconnectedBank={hasDisconnectedBank}
          />
        );
      } else if (missingPayments > 0) {
        return (
          <RentPaymentInProgressPaymentIdentified
            {...props}
            hideButton={false}
            hasDisconnectedBank={hasDisconnectedBank}
          />
        );
      } else {
        return (
          <RentPaymentInProgressPaymentIdentified
            {...props}
            hideButton={false}
            customTitle={true}
            rentReportingStatus={rentReportingStatus}
            rentReportingSubStatus={rentReportingSubStatus}
            hasDisconnectedBank={hasDisconnectedBank}
          />
        );
      }
    } else if (rentReportingSubStatus === WAITING_FOR_INFORMATION) {
      return (
        <RentPaymentInProgressPaymentIdentified {...props} hideButton={false} />
      );
    } else if (
      rentReportingSubStatus === VOR_SENT ||
      rentReportingSubStatus === READY_FOR_SUBMISSION
    ) {
      return (
        <RentPaymentInProgressPaymentIdentified
          {...props}
          hideButton={true}
          hasDisconnectedBank={hasDisconnectedBank}
        />
      );
    } else if (
      rentReportingSubStatus === READY_FOR_VERIFICATION ||
      rentReportingSubStatus === WAITING_FOR_PAYMENT ||
      rentReportingSubStatus === CALL_TO_VERIFY
    ) {
      return (
        <RentPaymentInProgressPaymentIdentified
          {...props}
          hideButton={false}
          hasDisconnectedBank={hasDisconnectedBank}
        />
      );
    } else if (rentReportingSubStatus === ESCALATED_VOR) {
      return (
        <RentPaymentInProgressPaymentIdentified {...props} hideButton={true} />
      );
    } else if (rentReportingSubStatus === UNABLE_TO_VERIFY) {
      return (
        <RentPaymentInProgressPaymentIdentified
          {...props}
          hideButton={true}
          customButtom={true}
        />
      );
    } else {
      return (
        <RentPaymentInProgressPaymentIdentified {...props} hideButton={true} />
      );
    }
  } else if (rentReportingStatus === UNENROLLED) {
    return <RentPaymentEnrollment {...props} />;
  } else {
    logger.error(
      `Unable to find UI for combination of ${rentReportingStatus} and ${rentReportingSubStatus}`,
    );
    return '';
  }
};

const MyRentalInfoContainer = props => {
  const { config } = useConfig();
  return (
    <SectionContainer
      className={`rentPaymentCardSection border-rad8px grayShadow16 padT20 padLR16 padB16 margB0 m-format100 m-margB2`}
    >
      <div className="text-16 m-text-14 margB24 m-margB20 bold">
        My Rental Info
      </div>
      <div className="text-14 lineHeightMedium margB0 m-format100">
        <Row className="padLR0 margR0 margB16">
          <div className="flex-container justify-between align-items-center padB14">
            <div className="FlexContainer flex-column">
              <div className="text14 margB0">
                <Button
                  className="marg0 pad0"
                  mrphType="link"
                  onClick={props.onClickRightArrowRentalProperties}
                  disabled={props.disableRentalPropertiesButton}
                >
                  {'Rental Property Details'}
                </Button>
              </div>
            </div>
            <div className="m-padLR0">
              <Button
                className="marg0 pad0"
                mrphType="link"
                onClick={props.onClickRightArrowRentalProperties}
                disabled={props.disableRentalPropertiesButton}
              >
                <ArrowRight aria-label="arrow-right-icon" />
              </Button>
            </div>
          </div>
          <div className="borderTpGray050 margLR0" />
        </Row>

        <Row className="padLR0 margLR0 margB16">
          <div className="flex-container justify-between align-items-center padB14">
            <div className="FlexContainer flex-column">
              <div className="text14 margB0">
                <Button
                  className="marg0 pad0"
                  mrphType="link"
                  onClick={props.onClickRightArrowRentalPayments}
                  disabled={props.disableRentalPaymentsButton}
                >
                  {'Rental Payments'}
                </Button>
              </div>
            </div>
            <div className="m-padLR0">
              <Button
                className="marg0 pad0"
                mrphType="link"
                onClick={props.onClickRightArrowRentalPayments}
                disabled={props.disableRentalPaymentsButton}
              >
                <ArrowRight aria-label="arrow-right-icon" />
              </Button>
            </div>
          </div>
          <div className="borderTpGray050 margLR0" />
        </Row>
        <Row className="padLR0 margR0 margB16">
          <div className="flex-container justify-between align-items-center padB14">
            <div className="FlexContainer flex-column">
              <div className="text14 margB0">
                <Button
                  className="marg0 pad0"
                  mrphType="link"
                  onClick={props.onClickRightArrowLinkedBankAccounts}
                  disabled={props.disableLinkedBankAccountsButton}
                >
                  {'Linked Bank Accounts'}
                </Button>
              </div>
            </div>
            {props.paymentsResponse &&
              props.paymentsResponse.hasDisconnectedBanks && (
                <div className="m-padLR0">
                  <CSTooltip
                    title={config?.rentpayment?.link_bank_accounts?.tooltip}
                    isInline={true}
                  >
                    <BankAccountDisconnected aria-label="bank-account-disconnected-icon" />
                  </CSTooltip>
                </div>
              )}
            <div className="m-padLR0">
              <Button
                className="marg0 pad0"
                mrphType="link"
                onClick={props.onClickRightArrowLinkedBankAccounts}
                disabled={props.disableLinkedBankAccountsButton}
              >
                <ArrowRight aria-label="arrow-right-icon" />
              </Button>
            </div>
          </div>
          <div className="borderTpGray050 margLR0" />
        </Row>
        <Row className="padLR0 margR0">
          <div className="flex-container justify-between align-items-center padB0">
            <div className="FlexContainer flex-column">
              <div className="text14 margB0">
                <Button
                  className="marg0 pad0"
                  mrphType="link"
                  onClick={props.onClickRightArrowMyDocuments}
                  disabled={props.disableMyDocumentsButton}
                >
                  {'My Documents'}
                </Button>
              </div>
            </div>
            <div className="m-padLR0">
              <Button
                className="marg0 pad0"
                mrphType="link"
                onClick={props.onClickRightArrowMyDocuments}
                disabled={props.disableMyDocumentsButton}
              >
                <ArrowRight aria-label="arrow-right-icon" />
              </Button>
            </div>
          </div>
        </Row>
      </div>
    </SectionContainer>
  );
};

const LastestPaymentStatusContainer = props => {
  return (
    <SectionContainer
      className={`FlexContainer flex-column rentPaymentCardSection border-rad8px grayShadow16 padT20 padL16 padR16 padB4 margB0 m-format100 m-margB16`}
    >
      <div className="flex-container justify-between align-items-center padB26">
        <div className="FlexContainer flex-column">
          <div className="text-16 m-text-14 margB0 m-margB0 bold">
            {'Latest Payment Status'}
          </div>
        </div>
        <div className="m-padL0 m-padR0">
          <Button
            className="marg0 m-marg0 pad0 m-pad0"
            mrphType="link"
            onClick={props.onClickSeeAllLatestPayments}
          >
            See All
          </Button>
        </div>
      </div>

      <div className="text-14">
        {props.userFilteredPayments &&
          props.userFilteredPayments.map((item, index) => {
            let status = '';
            if (item.status === 'reported') {
              status = 'Reported';
            } else if (item.status === 'submitted') {
              status = 'Submitted';
            } else if (item.status === 'missing') {
              status = 'Missing';
            }
            return (
              <Row
                key={index}
                className="padL0 padR0 margR0 m-margR0 m-padR0 margB16 m-padL0"
              >
                <div className="flex-container justify-between align-items-center padB16">
                  <div className="FlexContainer flex-column desktopHide">
                    <div className="text14  margB0 fontGray100">
                      {`${formatAsMonthDayYear(item.date)} Payment ${status}`}
                    </div>
                    <div className="text14  margB0">
                      {`${fixedNumOfLetters(item.name, 15)}`}
                    </div>
                    <div className="text14  margB0">
                      {`${formatIncome(item.amount, true)}`}
                    </div>
                  </div>

                  <div className="FlexContainer flex-column mobileHide">
                    <div className="text14  margB0">
                      {`${formatAsMonthDayYear(item.date)} Payment ${status}`}
                    </div>
                  </div>
                  <div className="FlexContainer flex-column mobileHide">
                    <div className="text14  margB0">
                      {`${fixedNumOfLetters(item.name, 15)}`}
                    </div>
                  </div>
                  <div className="FlexContainer flex-column mobileHide">
                    <div className="text14  margB0">
                      {`${formatIncome(item.amount, true)}`}
                    </div>
                  </div>

                  <div className="m-padL0 m-padR0">
                    {item.status === 'missing' && <PaymentPending />}
                    {item.status === 'submitted' && <PaymentPending />}
                    {item.status === 'reported' && <PaymentPosted />}
                  </div>
                </div>
                {props.userFilteredPayments &&
                  index !== props.userFilteredPayments.length - 1 && (
                    <div className="borderTpGray050 margL0 margR0"></div>
                  )}
              </Row>
            );
          })}
      </div>
    </SectionContainer>
  );
};

//Please delete this if possible
// const UnenrolledInfoContainer = props => {
//   return (
//     <SectionContainer
//       className={`FlexContainer flex-column rentPaymentCardSection border-rad8px grayShadow16 padT20 padL16 padR16 padB16 margB0 m-format100 m-margB16`}
//     >
//       <div className="unenrolledInfoContainer-descriptions">
//         <Row className="padL0 padR0 margR0 m-margR0 m-padR0 margB16 m-padL0">
//           <div className="flex-container justify-between align-items-center padB16">
//             <div className="FlexContainer flex-column">
//               <div className="text-24 bold margB0 padT12 padB12 padL20 padR20 lineHeight-1-33 margAuto text-center">
//                 {"We couldn't verify your rental info"}
//               </div>
//               <div className="text-14 margB0 padT12 padL20 padR20 padB32 lineHeight-1-33 margAuto text-center">
//                 {
//                   'We are contacting your landlord to verify your rental information. This usually takes 3-5 business days. Once complete, your latest payments will be reported here.'
//                 }
//               </div>
//               <div className="mobileHide margAuto">
//                 <Button
//                   mrphType="primary"
//                   onClick={props.onClickContactSupportlink}
//                   className="format100"
//                 >
//                   {props.rentPaymentTexts['button']}
//                 </Button>
//               </div>
//             </div>
//           </div>
//         </Row>
//       </div>
//     </SectionContainer>
//   );
// };

const LastestPaymentStatusNoContentContainer = props => {
  return (
    <SectionContainer
      className={`FlexContainer flex-column rentPaymentCardSection border-rad8px grayShadow16 padT20 padL16 padR16 padB16 margB0 m-format100 m-margB16`}
    >
      <div className="flex-container justify-between align-items-center padB16">
        <div className="FlexContainer flex-column">
          <div className="text-16 m-text-14 margB0 m-margB0 bold">
            {'Latest Payment Status'}
          </div>
        </div>
        <div className="m-padL0 m-padR0">
          <Button
            className="marg0 m-marg0 pad0 m-pad0"
            mrphType="link"
            disabled={true}
            onClick={props.onClickSeeAllPayments}
          >
            See All
          </Button>
        </div>
      </div>

      <div className="text-14">
        <Row className="padL0 padR0 margR0 m-margR0 m-padR0 margB16 m-padL0">
          <div className="flex-container justify-between align-items-center padB16">
            <div className="FlexContainer flex-column">
              <div className="text12 fontGray100 bgGray005 margB0 padT48 padB52 padL20 padR20 lineHeight-1-33">
                {
                  'We are contacting your landlord to verify your rental information. This usually takes 3-5 business days. Once complete, your latest payments will be reported here.'
                }
              </div>
            </div>
          </div>
        </Row>
      </div>
    </SectionContainer>
  );
};

const rentPaymentAuthenticateButtonAction = props => {
  props.onRentPaymentTracking(props);
  props.onEnrollRentPaymentAuthenticate();
};

const rentPaymentRentalInfoButtonAction = (props, action) => {
  props.onRentPaymentTracking(props);
  props.onRentPaymentRentalInfo(action);
};

const RentPaymentSignUp = props => {
  const { displayClasses, onVisibilityChange, getRentPaymentText, userInfo } =
    props;
  const rentPaymentTexts = getRentPaymentText(props);
  const planName = getPremiumPlanUiDisplayName(userInfo);
  useViewModule({
    'Module Name': 'Report Your Rent Payments - Returning user',
    'Module Position': props.modulePosition,
  });
  const isCustomButton = props.customButtom ? true : false;
  return (
    <div className="RentPaymentDashboard pos-rel">
      <Row className="flex-container flex-row justify-between padL0 padR0 m-padB0 margB0 pos-abs rentPaymentDashboardTag mobileHide">
        <FlexContainer className="align-items-center justify-center">
          <Tag
            backgroundClass="bgYellow075"
            extClass="text-10 padB8 padT8 padL16 padR16 bgPurple0 purple400 bold"
          >
            {planName}
          </Tag>
        </FlexContainer>
      </Row>
      <SectionContainer className={`${displayClasses}  m-padT20`}>
        <VisibilitySensor onChange={onVisibilityChange}>
          <div className="invisible-pixel" />
        </VisibilitySensor>
        <Row className="padR80 margR24 m-margR0 m-padR16 padT24">
          <div className="flex-container justify-between m-flex-column">
            <div className="FlexContainer flex-column">
              <Label typeHeader={'h3'} className="H3 margB16 mobileHide">
                Report Your Rent Payments
              </Label>
              <div className="m-text-24 bold margB16 desktopHide">
                <FlexContainer className="align-items-center">
                  Rent Payments
                  <Tag extClass="text-10 padB8 padT8 padL16 padR16 bgPurple0 purple400 bold margL20 ">
                    {planName}
                  </Tag>
                </FlexContainer>
              </div>
              <div className="text-14 lineHeightMedium margB24 mobile-Body-1 format66 m-format100 mobileHide">
                <b>{rentPaymentTexts['title']}</b>{' '}
                {rentPaymentTexts['subTitle']}
              </div>
              {!props.hideButton && isCustomButton && (
                <div className="mobileHide">
                  <Button
                    mrphType="primary"
                    onClick={props.onClickContactSupportlink}
                    className="format48"
                  >
                    {rentPaymentTexts['button']}
                  </Button>
                </div>
              )}
              {!props.hideButton && !isCustomButton && (
                <div className="mobileHide">
                  <Button
                    mrphType="primary"
                    onClick={() => {
                      rentPaymentAuthenticateButtonAction(props);
                    }}
                    className="format48"
                  >
                    {rentPaymentTexts['button']}
                  </Button>
                </div>
              )}
            </div>
            <div className="m-margB24 m-padL8 m-padR16 ">
              <RentPayment
                className="rentPaymentDashboardImage"
                aria-label="rentpayment-icon"
              />
            </div>
            <div className="desktopHide">
              <SectionContainer
                className={`rentPaymentCardSection border-rad8px grayShadow16 pad14 format32 m-format100 pos-rel padB24 padT24 m-margB2`}
              >
                <div className="m-text-10 margB4 fontGray100">STATUS</div>
                <div className="m-text-16 margB8 bold">
                  {rentPaymentTexts['title']}
                </div>
                <div className="text-14 lineHeightMedium margB24 format66 m-format100">
                  {rentPaymentTexts['subTitle']}
                </div>
                {!props.hideButton && isCustomButton && (
                  <Button
                    mrphType="primary"
                    onClick={props.onClickContactSupportlink}
                  >
                    {rentPaymentTexts['button']}
                  </Button>
                )}
                {!props.hideButton && !isCustomButton && (
                  <Button
                    mrphType="primary"
                    onClick={() => {
                      rentPaymentAuthenticateButtonAction(props);
                    }}
                  >
                    {rentPaymentTexts['button']}
                  </Button>
                )}
              </SectionContainer>
              {props.includeLinks && (
                <>
                  <div className="pad8"></div>
                  <MyRentalInfoContainer {...props} />
                </>
              )}
            </div>
          </div>
        </Row>
      </SectionContainer>
    </div>
  );
};

/* Unenrolled module for vor rejected */
// Please consider deleting this instead of commenting it out
//
// const RentPaymentUnenrolled = props => {
//   const { displayClasses, onVisibilityChange, getRentPaymentText } = props;
//   const rentPaymentTexts = getRentPaymentText(props);
//   useViewModule({
//     'Module Type': 'Report Your Rent Payments - Returning user',
//   });
//   useViewModule({ 'Module Type': 'My Rental Info' });
//   useRentPaymentDashboardViewOffer(props.eventHandler);
//   const isCustomButton = props.customButtom ? true : false;
//   return (
//     <div className="RentPaymentDashboard pos-rel">
//       {/* Desktop container */}
//       <SectionContainer
//         className={`${displayClasses} padT24 padB24 m-padB0 m-padT24  m-margB0`}
//       >
//         <VisibilitySensor onChange={onVisibilityChange}>
//           <div className="invisible-pixel" />
//         </VisibilitySensor>
//         <Row className="flex-container flex-row justify-between m-padB12 margB0">
//           <FlexContainer className="align-items-center justify-center">
//             <div className="m-text-24 bold margB24 m-margB0">
//               <FlexContainer className="align-items-center">
//                 <div className="H3">Rent Payments</div>
//                 <Tag extClass="text-10 padB8 padT8 padL16 padR16 bgPurple0 purple400 bold margL20 ">
//                   PREMIUM
//                 </Tag>
//               </FlexContainer>
//             </div>
//           </FlexContainer>
//         </Row>
//         {/* Top block desktop */}
//         <Row className="padR48 margR0 m-margR0 m-padR16 margB16 m-margB0">
//           <div className="flex-container justify-between m-flex-column">
//             <div className="mobileHide format100">
//               <div className="flex-container rentPaymentCardSection border-rad8px grayShadow16 format100 pad16">
//                 <div className="FlexContainer flex-column mobileHide format66 m-format100">
//                   <div className="text-12 bold m-text-10 margB4 fontGray100">
//                     STATUS
//                   </div>
//                   <div className="m-text-16 margB8 bold">
//                     {rentPaymentTexts['title']}
//                   </div>
//                   <div className="text-14 lineHeightMedium margB0">
//                     {rentPaymentTexts['subTitle']}
//                   </div>
//                 </div>
//                 <div className="flex-container align-items-center format33 m-format100 justify-end">
//                   <div className="m-margB24 m-padL8 m-padR16">
//                     {!props.hideButton && isCustomButton && (
//                       <div className="desktopHide">
//                         <Button
//                           mrphType="primary"
//                           onClick={props.onClickContactSupportlink}
//                           className="format48"
//                         >
//                           {rentPaymentTexts['button']}
//                         </Button>
//                       </div>
//                     )}
//                     {!props.hideButton && !isCustomButton && (
//                       <div className="desktopHide">
//                         <Button
//                           mrphType="primary"
//                           onClick={() => {
//                             rentPaymentAuthenticateButtonAction(props);
//                           }}
//                           className="format48"
//                         >
//                           {rentPaymentTexts['button']}
//                         </Button>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </Row>
//         {/* Bottom block desktop only */}
//         <Row className="padR24 margR24 m-margR0 m-padR16 ">
//           <div className="flex-container justify-between m-flex-column">
//             <div className="format68 mobileHide">
//               <UnenrolledInfoContainer
//                 {...props}
//                 rentPaymentTexts={rentPaymentTexts}
//               />
//             </div>
//             <div className="m-margB24 m-padL8 m-padR16 mobileHide padL16 padR0 padT0 padB0 format32">
//               <MyRentalInfoContainer {...props} />
//             </div>
//           </div>
//         </Row>
//       </SectionContainer>
//       {/* Mobile container */}
//       <div className="desktopHide">
//         <SectionContainer className={`${displayClasses} padT72 m-padT0 `}>
//           <Row className="padR80 margR24 m-margR0 m-padR16 bgWhite">
//             <div className="flex-container justify-between m-flex-column">
//               <div className="m-margB24 m-padL8 m-padR16 desktopHide">
//                 <RentPayment className="rentPaymentDashboardImage" />
//               </div>
//               <div className="desktopHide">
//                 <SectionContainer
//                   className={`rentPaymentCardSection border-rad8px grayShadow16 pad14 format32 m-format100 pos-rel padB24 padT24 m-margB2`}
//                 >
//                   <div className="m-text-10 margB4 fontGray100">STATUS</div>
//                   <div className="m-text-16 margB8 bold">
//                     {rentPaymentTexts['title']}
//                   </div>
//                   <div className="text-14 lineHeightMedium margB24 format66 m-format100">
//                     {rentPaymentTexts['subTitle']}
//                   </div>
//                   {!props.hideButton && isCustomButton && (
//                     <Button
//                       mrphType="primary"
//                       onClick={props.onClickContactSupportlink}
//                     >
//                       {rentPaymentTexts['button']}
//                     </Button>
//                   )}
//                   {!props.hideButton && !isCustomButton && (
//                     <Button
//                       mrphType="primary"
//                       onClick={() => {
//                         rentPaymentAuthenticateButtonAction(props);
//                       }}
//                     >
//                       {rentPaymentTexts['button']}
//                     </Button>
//                   )}
//                 </SectionContainer>
//                 {props.includeLinks && (
//                   <>
//                     <div className="pad8"></div>
//                     <MyRentalInfoContainer {...props} />
//                   </>
//                 )}
//               </div>
//             </div>
//           </Row>
//         </SectionContainer>
//       </div>
//     </div>
//   );
// };

const RentPaymentEnrollment = props => {
  const {
    displayClasses,
    onVisibilityChange,
    rentReportingPreQualification,
    userInfo,
  } = props;
  const showRentReportingQualifyButton =
    rentReportingPreQualification?.isEnabled &&
    rentReportingPreQualification?.config?.check_qualify_link_button?.show;
  const planName = getPremiumPlanUiDisplayName(userInfo);
  useViewModule({
    'Module Name': 'Report Your Rent Payments - Returning user',
    'Module Position': props.modulePosition,
  });
  return (
    <div className="RentPaymentDashboard pos-rel">
      <Row className="flex-container flex-row justify-between padL0 padR0 m-padB0 margB0 pos-abs rentPaymentDashboardTag mobileHide">
        <FlexContainer className="align-items-center justify-center">
          <Tag
            backgroundClass="bgYellow075"
            extClass="text-10 padB8 padT8 padL16 padR16 bgPurple0 purple400 bold"
          >
            {planName}
          </Tag>
        </FlexContainer>
      </Row>
      <SectionContainer className={`${displayClasses} m-padT20`}>
        <VisibilitySensor onChange={onVisibilityChange}>
          <div className="invisible-pixel" />
        </VisibilitySensor>
        <Row className="padR80 margR24 m-margR0 m-padR16">
          <div className="flex-container justify-between m-flex-column">
            <div className="FlexContainer flex-column">
              <Label typeHeader={'h3'} className="H3 margB16 mobileHide">
                Report Your Rent Payments
              </Label>
              <div className="m-text-24 bold margB16 desktopHide">
                <FlexContainer className="align-items-center">
                  Rent Payments
                  <Tag extClass="text-10 padB8 padT8 padL16 padR16 bgPurple0 purple400 bold margL20 ">
                    {planName}
                  </Tag>
                </FlexContainer>
              </div>
              <div className="text-14 lineHeightMedium margB24 mobile-Body-1 format66 m-format100 mobileHide">
                <b>{'Get Credit for Your Rent Payments.'}</b>{' '}
                {
                  "We'll automatically report up to 24 Months of rental history,  payment track record &  Account mix."
                }
              </div>
              <FlexContainer className="mobileHide">
                <Button
                  mrphType="primary"
                  onClick={props.onClickRentPaymentReporting}
                  className="format48"
                >
                  {'Set Up Rent Reporting Now'}
                </Button>
                {showRentReportingQualifyButton && (
                  <Button
                    mrphType="link"
                    onClick={
                      rentReportingPreQualification.onClickRentReportingPreQualFlow
                    }
                  >
                    {
                      rentReportingPreQualification?.config
                        ?.check_qualify_link_button?.text
                    }
                  </Button>
                )}
              </FlexContainer>
            </div>
            <div className="m-margB24 m-padL8 m-padR16 ">
              <RentPayment className="rentPaymentDashboardImage" />
            </div>
            <div className="desktopHide">
              <SectionContainer
                className={`rentPaymentCardSection border-rad8px grayShadow16 pad14 format32 m-format100 pos-rel padB24 padT24 m-margB2`}
              >
                <div className="m-text-10 margB4 fontGray100">STATUS</div>
                <div className="m-text-16 margB8 bold">
                  {'Get Credit for Your Rent Payments.'}
                </div>
                <div className="text-14 lineHeightMedium margB24 format66 m-format100">
                  {
                    "We'll automatically report up to 24 Months of rental history,  payment track record &  Account mix."
                  }
                </div>
                <Button
                  mrphType="primary"
                  onClick={props.onClickRentPaymentReporting}
                >
                  {'Set Up Rent Reporting Now'}
                </Button>
                {showRentReportingQualifyButton && (
                  <Button
                    mrphType="link"
                    className="format100"
                    onClick={
                      rentReportingPreQualification.onClickRentReportingPreQualFlow
                    }
                  >
                    {
                      rentReportingPreQualification?.config
                        ?.check_qualify_link_button?.text
                    }
                  </Button>
                )}
              </SectionContainer>
            </div>
          </div>
        </Row>
      </SectionContainer>
    </div>
  );
};

const ShowMessage = props => {
  if (props.isDisplayCMAlertMessage) {
    return <ShowDelightMessage {...props} />;
  } else {
    return <ShowStatusMessage {...props} />;
  }
};

const ShowStatusMessage = props => {
  const { rentPaymentTexts } = props;
  const enrollmentStatus = props?.authResponse?.enrollmentStatus;
  const rentReportingSubStatus =
    enrollmentStatus?.subStatus || props?.userInfo?.rentReporting?.subStatus;
  const customTitle = props.customTitle ? true : false;
  const isCustomButton = props.customButtom ? true : false;
  const showCTAButton =
    !props.isDisplayCMAlertMessage &&
    ((props.hideButton && isCustomButton) ||
      (!props.hideButton && rentPaymentTexts['button']));

  const CTAButtonContainerClasses = classnames({
    ' format66': showCTAButton,
  });
  const hasDisconnectedBanks = props?.paymentsResponse?.hasDisconnectedBanks;
  const tiTleStatusClasses = classnames({
    ' fontRed4 ': hasDisconnectedBanks,
    fontOrange100: rentReportingSubStatus === VOR_SENT,
  });
  const showButton = !props.hideButton && rentPaymentTexts['button'];

  return (
    <>
      <div className="mobileHide format100">
        <div
          className={`flex-container rentPaymentCardSection grayShadow16 border-rad8px format100 pad16`}
        >
          <div
            className={`FlexContainer flex-column mobileHide ${CTAButtonContainerClasses} m-format100`}
          >
            <div className={`text-12 bold m-text-10 margB4 fontGray100`}>
              STATUS
            </div>
            <div className={`m-text-16 margB8 bold ${tiTleStatusClasses}`}>
              {!customTitle && rentPaymentTexts['title']}
              {customTitle && <CustomTitlePerRentReportingStatus {...props} />}
            </div>
            <div className="text-14 lineHeightMedium margB0">
              {rentPaymentTexts['subTitle']}
            </div>
          </div>
          {showCTAButton && (
            <div className="flex-container align-items-center format33 m-format100 justify-end">
              <div className="m-margB24 m-padL8 m-padR16">
                {props.hideButton && isCustomButton && (
                  <Button
                    mrphType="primary"
                    onClick={props.onClickContactSupportlink}
                  >
                    {rentPaymentTexts['button']}
                  </Button>
                )}
                {showButton && (
                  <Button
                    mrphType="primary"
                    onClick={() => {
                      rentPaymentRentalInfoButtonAction(
                        props,
                        rentPaymentTexts['action'],
                      );
                    }}
                    disabled={props.disableRentStatusButton}
                  >
                    {rentPaymentTexts['button']}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/** Mobile containers */}
      <div className="desktopHide">
        <SectionContainer
          className={`rentPaymentCardSection grayShadow16 border-rad8px pad14 format32 m-format100 pos-rel padB24 padT24 m-margB16`}
        >
          <div className={`m-text-10 margB4 fontGray100`}>STATUS</div>
          <div className={`m-text-16 margB8 bold ${tiTleStatusClasses}`}>
            {!customTitle && rentPaymentTexts['title']}
            {customTitle && <CustomTitlePerRentReportingStatus {...props} />}
          </div>
          <div className="text-14 lineHeightMedium format66 m-format100">
            {rentPaymentTexts['subTitle']}
          </div>
          {props.hideButton && isCustomButton && (
            <Button
              mrphType="primary"
              className={`margT24`}
              onClick={props.onClickContactSupportlink}
            >
              {rentPaymentTexts['button']}
            </Button>
          )}
          {showButton && (
            <Button
              mrphType="primary"
              className={`margT24`}
              onClick={() => {
                rentPaymentRentalInfoButtonAction(
                  props,
                  rentPaymentTexts['action'],
                );
              }}
              disabled={props.disableRentStatusButton}
            >
              {rentPaymentTexts['button']}
            </Button>
          )}
        </SectionContainer>
      </div>
    </>
  );
};

const ShowDelightMessage = props => {
  const { creditInfo, rentPaymentTexts } = props;
  const attr = delightMessageMetaData(creditInfo);

  useViewModule({
    'Module Name': 'Rent Reporting- Returning user',
    Status: 'Enrolled',
    'Module Position': props.modulePosition,
    SubStatus: 'Rental Tradeline Created',
    ...attr,
  });

  return (
    <>
      <div className="mobileHide format100">
        <div
          className={`flex-container rentPaymentCardSection delightCardSection bgYellow0 border-rad8px format100 pad16`}
        >
          <div className={`FlexContainer flex-column mobileHide m-format100`}>
            <div className={`text-12 bold m-text-10 margB4 fontYellow200`}>
              STATUS
            </div>
            <div className={`m-text-16 margB8 bold`}>
              {rentPaymentTexts['title']}
            </div>
            <div className="text-14 lineHeightMedium margB0">
              {rentPaymentTexts['subTitle']}
            </div>
          </div>
          <LightBulb className={`justify-end`} />
        </div>
      </div>
      {/** Mobile containers */}
      <div className="desktopHide">
        <SectionContainer
          className={`rentPaymentCardSection delightCardSection bgYellow0 border-rad8px pad14 format32 m-format100 pos-rel padB24 padT24 m-margB16`}
        >
          <LightBulb className={`pos-abs delightCardImage`} />
          <div className={`m-text-10 margB4 fontYellow200`}>STATUS</div>
          <div className={`m-text-16 margB8 bold`}>
            {rentPaymentTexts['title']}
          </div>
          <div className="text-14 lineHeightMedium format66 m-format100">
            {rentPaymentTexts['subTitle']}
          </div>
        </SectionContainer>
      </div>
    </>
  );
};

const RentPaymentInProgressPaymentIdentified = props => {
  const enrollmentStatus = props?.authResponse?.enrollmentStatus;
  const rentReportingStatus =
    enrollmentStatus?.status || props?.userInfo?.rentReporting?.status;
  const rentReportingSubStatus =
    enrollmentStatus?.subStatus || props?.userInfo?.rentReporting?.subStatus;
  const { displayClasses, onVisibilityChange, getRentPaymentText } = props;
  const rentPaymentTexts = getRentPaymentText(props);

  useViewModule({
    'Module Name': 'Rent Payment Status',
    'Module Position': props.modulePosition,
    'Rent Payment Status': rentPaymentTexts['title'],
  });
  useViewModule({
    'Module Name': 'Latest Payment Status',
    'Module Position': props.modulePosition,
    Status: rentReportingStatus,
    SubStatus: rentReportingSubStatus,
  });

  useViewModule({
    'Module Name': 'My Rental Info',
    'Module Position': props.modulePosition,
  });

  const userHasPayments =
    props.paymentsResponse &&
    props.paymentsResponse.payments &&
    props.paymentsResponse.payments.length > 0;
  const userFilteredPayments = userHasPayments
    ? props.paymentsResponse.payments.filter(item => item.status !== 'missing')
    : {};
  const userHasValidPayments = userFilteredPayments.length > 0;
  const bankAccountUnlinkedBoldText = 'Important:';
  const bankAccountUnlinkedText =
    'Your bank account is no longer linked to Credit Sesame for Rent Reporting.  Click "Linked Bank Accounts" to fix it.';

  return (
    <div className="RentPaymentDashboard">
      <SectionContainer
        className={`${displayClasses} padT24 padB24 m-padB0 m-padT24`}
      >
        <VisibilitySensor onChange={onVisibilityChange}>
          <div className="invisible-pixel" />
        </VisibilitySensor>
        <Row className="flex-container flex-row justify-between m-padB0 margB0 ">
          <FlexContainer className="align-items-center justify-center">
            <div className="m-text-24 bold margB24">
              <FlexContainer className="align-items-center">
                <Label className="H3" typeHeader={'h3'}>
                  Rent Payments
                </Label>
                <Tag extClass="text-10 padB8 padT8 padL16 padR16 bgPurple0 purple400 bold margL20 ">
                  PREMIUM
                </Tag>
              </FlexContainer>
            </div>
          </FlexContainer>
        </Row>
        {/* Top block desktop */}
        <Row className="padR48 margR0 m-margR0 m-padR16 margB16">
          <div className="flex-container justify-between m-flex-column">
            <ShowMessage {...props} rentPaymentTexts={rentPaymentTexts} />

            <div className="desktopHide">
              {props?.hasDisconnectedBank && (
                <RowBox className="margB16">
                  <Label>
                    <Label type="inline bold" className="toUpper margR6">
                      {bankAccountUnlinkedBoldText}
                    </Label>
                    {bankAccountUnlinkedText}
                  </Label>
                </RowBox>
              )}
              {userHasValidPayments && (
                <LastestPaymentStatusContainer
                  {...props}
                  userFilteredPayments={userFilteredPayments}
                />
              )}
              {!userHasValidPayments && (
                <LastestPaymentStatusNoContentContainer {...props} />
              )}
              <MyRentalInfoContainer {...props} />
            </div>
          </div>
        </Row>
        {/* Bottom block desktop only */}
        <Row className="padR24 margR24 m-margR0 m-padR16 ">
          <div className="flex-container justify-between m-flex-column">
            <div className="format68 mobileHide">
              {userHasValidPayments && (
                <LastestPaymentStatusContainer
                  {...props}
                  userFilteredPayments={userFilteredPayments}
                />
              )}
              {!userHasValidPayments && (
                <LastestPaymentStatusNoContentContainer {...props} />
              )}
              {props?.hasDisconnectedBank && (
                <RowBox>
                  <Label>
                    <Label type="inline bold" className="toUpper margR6">
                      {bankAccountUnlinkedBoldText}
                    </Label>
                    {bankAccountUnlinkedText}
                  </Label>
                </RowBox>
              )}
            </div>

            <div className="m-margB24 m-padL8 m-padR16 mobileHide padL16 padR0 padT0 padB0 format32">
              <MyRentalInfoContainer {...props} />
            </div>
          </div>
        </Row>
      </SectionContainer>
    </div>
  );
};
export const CustomTitlePerRentReportingStatus = props => {
  return (
    <>
      {
        "Your current month's Rent has been reported to the Credit Bureaus! Check your "
      }
      <Link
        className="link-style no-line"
        onClickCallback={props.onClickAMlink}
      >
        account mix
      </Link>
      {' & '}
      <Link
        className="link-style no-line"
        onClickCallback={props.onClickPHlink}
      >
        payment history
      </Link>
      {'.'}
    </>
  );
};

export const RentPaymentDashboard = props => {
  return <RentPaymentBody {...props} />;
};
